import React, {useMemo} from 'react';
import {AppShortcut} from "../hooks";
import './app-shortcuts-config.scss';
import ButtonV2 from "../../shared/button-v2/button-v2";
import CustomModalV2 from "../../shared/custom-modal-v2/custom-modal-v2";
import useUserStorage from "../../../hooks/useUserStorage";
import {defaultAppShortcuts, inbuiltShortcuts} from "../hooks/preferences/preferencesDefaults";
import ShortcutCard from "./shortcut-card";

interface AppShortcutsConfigProps {
    shortcuts: AppShortcut[];
    onChange: (shortcuts: AppShortcut[]) => void;
}

const extractIconClass = (iconHtml: string): string => {
    const div = document.createElement('div');
    div.innerHTML = iconHtml;
    const iElement = div.querySelector('i');
    return iElement ? iElement.className : '';
}


const AppShortcutsConfig: React.FC<AppShortcutsConfigProps> = ({shortcuts, onChange}) => {
    const [openCreateModal, setOpenCreateModal] = React.useState(false);
    const [openSelectModal, setOpenSelectModal] = React.useState(false);
    const [editingShortcutIndex, setEditingShortcutIndex] = React.useState<number>(null);
    const [iconInput, setIconInput] = React.useState<string>("");
    const [nameInput, setNameInput] = React.useState<string>("");
    const [urlInput, setUrlInput] = React.useState<string>("");
    const [editingShortcutForm, setEditingShortcutForm] = React.useState<AppShortcut>(null);
    const myShortcutsStore = useUserStorage<AppShortcut[]>({
        key: 'myAppShortcuts',
        defaultValue: defaultAppShortcuts,
    });

    const editingShortcut = useMemo(() => {
        return editingShortcutIndex === null ? null : shortcuts[editingShortcutIndex];
    }, [editingShortcutIndex, shortcuts]);

    const handleAddShortcut = () => {
        const newShortcut: AppShortcut = {
            name: "",
            url: "#",
            icon: "",
        }
        onChange([...shortcuts, newShortcut]);
    }

    if (myShortcutsStore.loading || !shortcuts?.length) {
        return null;
    }

    return (
        <div className="app-shortcuts-config">
            <div className="app-shortcuts-config__list">
                {shortcuts?.map((shortcut, index) => {
                    return <ButtonV2 key={index} title={shortcut.name} width={50} onClick={() => {
                        setOpenSelectModal(true);
                        setEditingShortcutIndex(index);
                    }}>
                        <i className={shortcut.icon}></i>
                    </ButtonV2>
                })}
                {shortcuts.length < 6 &&
                    <button onClick={handleAddShortcut} className="app-shortcuts-config__add-shortcut-btn">
                        <i className="fa-solid fa-plus"></i>
                    </button>}
            </div>
            <CustomModalV2
                open={openSelectModal} onClose={() => setOpenSelectModal(false)}
                title="Seleccionar acceso directo"
                tertiaryButton={{
                    text: 'Vaciar',
                    onClick: () => {
                        const newShortcuts = [...shortcuts];
                        newShortcuts[editingShortcutIndex] = {
                            name: "",
                            url: "#",
                            icon: "",
                        };
                        onChange(newShortcuts);
                        setOpenSelectModal(false);
                        setEditingShortcutIndex(null);
                    },
                }}
                secondaryButton={{
                    text: 'Cerrar',
                    onClick: () => setOpenSelectModal(false),
                }}
                primaryButton={{
                    text: 'Eliminar',
                    onClick: () => {
                        const newShortcuts = shortcuts.filter((_, i) => i !== editingShortcutIndex);
                        onChange(newShortcuts);
                        setOpenSelectModal(false);
                        setEditingShortcutIndex(null);
                    },
                    variant: 'danger'
                }}
                footerLeftContent={
                    <ButtonV2 title="Crear nuevo acceso directo" variant="primary" size="small" onClick={() => {
                        setOpenCreateModal(true);
                        setOpenSelectModal(false);
                        setEditingShortcutForm(null);
                        setIconInput("");
                        setNameInput("");
                        setUrlInput("");
                    }}>
                        <i className="fa-solid fa-plus pe-2"></i> Nuevo
                    </ButtonV2>
                }>
                <div className="app-shortcuts-config__select-modal-content">
                    {editingShortcut?.name && <div>
                        <p>Modificando el acceso directo Nº{editingShortcutIndex + 1}: </p>
                        {editingShortcut && <ShortcutCard shortcut={editingShortcut}/>}
                    </div>}
                    {!editingShortcut?.name && <div>
                        <p>Elige tu acceso directo Nº{editingShortcutIndex + 1}: </p>
                    </div>}

                </div>
                <table className="table table-striped table-hover mt-3" style={{
                    maxHeight: 'calc(90vh - 370px)',
                    overflowY: 'auto',
                    display: 'block',
                    width: '100%',
                }}>
                    <thead style={{
                        width: '100%',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                    }}>
                    <tr>
                        <th scope="col" style={{
                            width: '40px',
                        }}>#
                        </th>
                        <th scope="col" style={{
                            width: "calc(40%)",
                        }}>Nombre
                        </th>
                        <th scope="col" style={{
                            width: "60%",
                        }}>Url
                        </th>
                    </tr>
                    </thead>
                    <tbody style={{
                        width: '100%',
                        position: "relative"
                    }}>
                    {[...inbuiltShortcuts, ...(myShortcutsStore.item ?? [])].map((shortcut, index) => {
                        const isUsed = shortcuts.some(s => s.url === shortcut.url);
                        return <tr className="app-shortcuts-config__shortcuts-table-row" key={index} onClick={() => {
                            if (isUsed) {
                                const usedIndex = shortcuts.findIndex(s => s.url === shortcut.url);
                                const newShortcuts = [...shortcuts];
                                newShortcuts[editingShortcutIndex] = newShortcuts[usedIndex];
                                newShortcuts[usedIndex] = shortcuts[editingShortcutIndex];
                                onChange(newShortcuts);
                            }
                            const newShortcuts = [...shortcuts];
                            newShortcuts[editingShortcutIndex] = shortcut;
                            onChange(newShortcuts);
                            setOpenSelectModal(false);
                            setEditingShortcutIndex(null);
                        }} style={{
                            cursor: 'pointer',
                        }}>
                            <th scope="row"><i className={shortcut.icon}></i></th>
                            <td>{shortcut.name}</td>
                            <td style={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '300px',
                            }}>{shortcut.url}
                                {!shortcut.disableEdit && <button
                                    className="app-shortcuts-config__edit-shortcut-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingShortcutForm(shortcut);
                                        setIconInput(`<i class="${shortcut.icon}"></i>`);
                                        setNameInput(shortcut.name);
                                        setUrlInput(shortcut.url);
                                        setOpenCreateModal(true);
                                        setOpenSelectModal(false);
                                    }}
                                    title="Editar acceso directo personalizado">
                                    <i className="fa-solid fa-pen-to-square"></i>
                                </button>}
                            </td>
                        </tr>
                    })}
                    </tbody>
                </table>
            </CustomModalV2>
            <CustomModalV2
                open={openCreateModal}
                primaryButton={{
                    text: 'Guardar',
                    onClick: () => {
                        const newShortcut: AppShortcut = {
                            name: nameInput || "Nuevo acceso directo",
                            url: urlInput ? urlInput.replace("https://squaddisk.web.app", "")
                                .replace("http://localhost:3000", "") : "#",
                            icon: extractIconClass(iconInput) || iconInput,
                        }

                        const newShortcuts = [...myShortcutsStore.item];
                        if (editingShortcutForm) {
                            const editingIndex = newShortcuts.findIndex(s => s.url === editingShortcutForm.url);
                            if (editingIndex !== -1) {
                                newShortcuts[editingIndex] = newShortcut;
                            }

                            const isUsedIndex = shortcuts.findIndex(s => s.url === editingShortcutForm.url);
                            if (isUsedIndex !== -1) {
                                const updatedShortcuts = [...shortcuts];
                                updatedShortcuts[isUsedIndex] = newShortcut;
                                onChange(updatedShortcuts);
                            }
                        } else {
                            newShortcuts.push(newShortcut);
                        }

                        myShortcutsStore.setItem(newShortcuts);

                        setIconInput("");
                        setNameInput("");
                        setUrlInput("");
                        setOpenCreateModal(false);
                        setOpenSelectModal(true);
                        setEditingShortcutForm(null);
                    },
                    disabled: !iconInput.trim() || !urlInput.trim(),
                }}
                secondaryButton={{
                    text: 'Cancelar',
                    onClick: () => {
                        setIconInput("");
                        setNameInput("");
                        setUrlInput("");
                        setOpenCreateModal(false);
                        if (editingShortcutIndex !== null) {
                            setOpenSelectModal(true);
                        }
                        setEditingShortcutForm(null);
                    },
                }}
                footerLeftContent={editingShortcutForm &&
                    <ButtonV2 title="Eliminar acceso directo" variant="danger" size="small" onClick={() => {
                        const newShortcuts = [...myShortcutsStore.item].filter(s => s.url !== editingShortcutForm.url);
                        myShortcutsStore.setItem(newShortcuts);
                        setIconInput("");
                        setNameInput("");
                        setUrlInput("");
                        setOpenCreateModal(false);
                        if (editingShortcutIndex !== null) {
                            setOpenSelectModal(true);
                        }
                        setEditingShortcutForm(null);
                    }}>
                        <i className="fa-solid fa-trash pe-2"></i> Eliminar
                    </ButtonV2>}
                onClose={() => {
                    setOpenCreateModal(false);
                    if (editingShortcutIndex !== null) {
                        setOpenSelectModal(true);
                    }
                }}
                title={editingShortcutForm ? "Editando acceso directo" : "Agregar acceso directo"}>
                <div className="app-shortcuts-config__create-modal-content">

                    <div className="mb-3">
                        <label htmlFor="name-input" className="form-label">Nombre</label>
                        <input type="text" className="form-control" id="name-input" placeholder="Mi perfil"
                               value={nameInput} onChange={(e) => setNameInput(e.target.value)}/>
                    </div>

                    <div className="mb-3">
                        <label htmlFor="icon-input" className="form-label">HTML del icono</label>
                        <div className="input-group">
                            <span className="input-group-text">
                                <i className={extractIconClass(iconInput) || "fa-solid fa-circle-question"}></i>
                            </span>
                            <input type="text" className="form-control" id="icon-input"
                                   value={iconInput}
                                   onChange={(e) => setIconInput(e.target.value)}
                                   placeholder={`<i class="fa-solid fa-user"></i>`}/>
                        </div>
                        <div className="form-text">
                            Encuentra los iconos en <a
                            href="https://fontawesome.com/search?ic=free-collection"
                            target="_blank"
                            rel="noreferrer">FontAwesome</a>. Copia la versión html
                            completa del icono.
                        </div>
                    </div>

                    <div className="mb-3">
                        <label htmlFor="url-input" className="form-label">Url destino</label>
                        <input type="text" className="form-control" id="url-input" placeholder="/my-profile/config"
                               value={urlInput} onChange={(e) => setUrlInput(e.target.value)}/>
                        <div className="form-text">
                            Puedes usar rutas internas de SquadDisk o rutas absolutas.
                        </div>
                    </div>
                </div>
            </CustomModalV2>
        </div>
    );
}

export default AppShortcutsConfig;
